/**
 * PacsAgGrid - AG Grid implementation for PACS study list
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridApi, ColumnApi, GridReadyEvent, RowClickedEvent, SelectionChangedEvent } from 'ag-grid-community';
import { Spin } from 'antd';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './PacsAgGrid.css';

import { ColumnHeaderActions } from './types';
import CustomColumnHeader from './CustomColumnHeader';

// Import cell renderers
import {
  StatusCellRenderer,
  ModalityCellRenderer,
  PatientNameCellRenderer,
  PatientIdCellRenderer,
  BodyPartCellRenderer,
  StudyDescCellRenderer,
  // SeriesCountCellRenderer,
  AccessionNumberCellRenderer,
  RefDoctorCellRenderer,
  DateTimeCellRenderer,
  ReportedByCellRenderer,
  SignedByCellRenderer,
  AssignedRadiologistCellRenderer,
  AssignedTechnicianCellRenderer,
} from './cellRenderers';

interface PacsAgGridProps {
  rowData: any[];
  loading?: boolean;
  columns: any[];
  fixedColumnsCount?: number;
  selectedRowKeys?: (string | number)[];
  onSelectionChange?: (keys: (string | number)[]) => void;
  onRowClick?: (record: any) => void;
  onColumnOrderChange?: (newOrder: any[]) => void;
  onOpenReport?: (record: any) => void;
  /** Open the viewer for a study (technician patient-name click, T009) */
  onOpenViewer?: (record: any) => void;
  onViewReport?: (record: any) => void;
  onOpenRadiant?: (studyUID: string) => void;
  enableRadiantViewer?: boolean;
  height?: string;
  /** Custom header actions per column (e.g., gear icon for settings) */
  columnHeaderActions?: ColumnHeaderActions;
  /** List of users for assignment dropdowns */
  userList?: any[];
  /** Callback when radiologist assignment changes */
  onRadiologistAssignment?: (record: any, value: string | undefined, rowIndex: number) => void;
  /** Callback when technician self-assigns (legacy - kept for compatibility) */
  onAssignToSelfTechnician?: (record: any, rowIndex: number) => void;
  /** Callback when any technician is assigned via the dropdown */
  onAssignTechnician?: (record: any, username: string, rowIndex: number) => void;
  /** Logged-in user details (for pinning self at top of technician dropdown) */
  currentUser?: any;
  /** User type for permission-based row selection */
  userType?: string;
  /** Callback to show all orders for a patient */
  onShowPatientOrders?: (record: any) => void;
  /** Callback to select a series */
  handleSelectSeries?: (record: any) => void;
  /**
   * Nameless, pinned-left icon columns rendered immediately after the checkbox
   * column (before Patient ID). These live outside the draggable / configurable
   * / saved-preference column system - they always appear and are never saved.
   * Each item is an Ant-style column def: { key, width, render }.
   */
  leadingColumns?: any[];
}

/**
 * Map column key to appropriate cell renderer
 */
const COLUMN_RENDERER_MAP: Record<string, React.FC<any>> = {
  'status': StatusCellRenderer,
  'po_status': StatusCellRenderer,
  'modality': ModalityCellRenderer,
  'po_modality': ModalityCellRenderer,
  'po_pin': PatientIdCellRenderer,
  'pat_pin': PatientIdCellRenderer,
  'body_part': BodyPartCellRenderer,
  'po_body_part': BodyPartCellRenderer,
  'diag_desc': StudyDescCellRenderer,
  'po_diag_desc': StudyDescCellRenderer,
  // 'ps_series_count': SeriesCountCellRenderer,
  'acc_no': AccessionNumberCellRenderer,
  'po_acc_no': AccessionNumberCellRenderer,
  'ref_doc': RefDoctorCellRenderer,
  'po_ref_doc': RefDoctorCellRenderer,
  'study_dt_tm': DateTimeCellRenderer,
  'ps_study_dt_tm': DateTimeCellRenderer,
  'scan_received_at': DateTimeCellRenderer,
  'ps_scan_received_at': DateTimeCellRenderer,
  'reported_by': ReportedByCellRenderer,
  'signed_by': SignedByCellRenderer,
  'po_signed_by': SignedByCellRenderer,
  'assigned_radiologist': AssignedRadiologistCellRenderer,
  'assigned_to_tech': AssignedTechnicianCellRenderer,
};

/**
 * Map column key to actual data path in row data.
 * Mirrors the data extraction logic used in cell renderers and shared column render functions.
 * Keys not listed here are assumed to exist at the top level of row data (e.g., ps_series_count, ps_study_dt_tm).
 */
const COLUMN_VALUE_PATH_MAP: Record<string, string> = {
  'status': 'order_workflow.ow_pacs_status',
  'po_status': 'order_workflow.ow_pacs_status',
  'modality': 'pacs_order.po_modality',
  'po_modality': 'pacs_order.po_modality',
  'pat_name': 'pacs_order.patient.pat_name',
  'po_pat_name': 'pacs_order.patient.pat_name',
  'po_pin': 'pacs_order.patient.pat_pin',
  'pat_pin': 'pacs_order.patient.pat_pin',
  'body_part': 'pacs_order.po_body_part',
  'po_body_part': 'pacs_order.po_body_part',
  'diag_desc': 'pacs_order.po_diag_desc',
  'po_diag_desc': 'pacs_order.po_diag_desc',
  'po_site': 'pacs_order.po_site',
  'po_visit_number': 'pacs_order.po_visit_number',
  'po_his_status': 'pacs_order.po_his_status',
  'acc_no': 'pacs_order.po_acc_no',
  'po_acc_no': 'pacs_order.po_acc_no',
  'ref_doc': 'pacs_order.po_ref_doc',
  'po_ref_doc': 'pacs_order.po_ref_doc',
  'po_ord_no': 'pacs_order.po_his_ord_no',
  'pat_age': 'pacs_order.patient.pat_age',
  'po_pat_age': 'pacs_order.patient.pat_age',
  'pat_sex': 'pacs_order.patient.pat_sex',
  'po_pat_sex': 'pacs_order.patient.pat_sex',
  'reported_by': 'reportedBy',
  'signed_by': 'signedBy',
  'po_signed_by': 'signedBy',
  'assigned_to_tech': 'order_workflow.ow_assigned_technician',
  'assigned_to': 'order_workflow.ow_assigned_technician',
  'assigned_radiologist': 'order_workflow.ow_assigned_rad_id',
};

/**
 * Helper to get nested object value by path string
 */
const getNestedValue = (obj: any, path: string | undefined): any => {
  if (!path || !obj) return undefined;
  return path.split('.').reduce((acc, part) => acc?.[part], obj);
};

/**
 * Extract header name from column title (handles React elements)
 */
const extractHeaderName = (col: any, index: number): string => {
  if (typeof col.title === 'string') {
    return col.title;
  }
  if (col.title?.props?.children) {
    const children = col.title.props.children;
    if (typeof children === 'string') {
      return children;
    }
    if (Array.isArray(children)) {
      return children.filter((c: any) => typeof c === 'string').join('') || '';
    }
    if (typeof children === 'object' && children?.props?.children) {
      return typeof children.props.children === 'string' ? children.props.children : '';
    }
  }
  return col.key || col.dataIndex || `Column ${index}`;
};

/**
 * Get cell renderer for a column - returns component reference, not inline function
 */
const getCellRenderer = (colKey: string, col: any): React.FC<any> | undefined => {
  // Patient name uses special renderer that accesses context
  if (colKey === 'pat_name' || colKey === 'po_pat_name') {
    return PatientNameCellRenderer;
  }

  // Actions column - use wrapper that calls col.render
  if (colKey === 'actions_column' || colKey === 'actions') {
    // Store render function reference on the column for access in renderer
    return (params: any) => {
      const renderFn = params.colDef?.cellRendererParams?.renderFn;
      if (renderFn) {
        try {
          return renderFn(null, params.data, params.rowIndex);
        } catch (err) {
          console.error('Actions render error:', err);
          return null;
        }
      }
      return null;
    };
  }

  // Look up renderer in the map
  if (COLUMN_RENDERER_MAP[colKey]) {
    return COLUMN_RENDERER_MAP[colKey];
  }

  // For columns with custom render functions
  if (col.render) {
    return (params: any) => {
      const renderFn = params.colDef?.cellRendererParams?.renderFn;
      if (renderFn) {
        try {
          return renderFn(null, params.data, params.rowIndex);
        } catch (err) {
          console.error('Cell render error:', err);
          return '';
        }
      }
      return '';
    };
  }

  return undefined;
};

const PacsAgGrid: React.FC<PacsAgGridProps> = ({
  rowData = [],
  loading = false,
  columns = [],
  fixedColumnsCount = 2,
  selectedRowKeys = [],
  onSelectionChange,
  onRowClick,
  onColumnOrderChange,
  onOpenReport,
  onOpenViewer,
  onViewReport,
  onOpenRadiant,
  enableRadiantViewer = false,
  height = 'calc(100vh - 27rem)',
  columnHeaderActions,
  userList = [],
  onRadiologistAssignment,
  onAssignToSelfTechnician,
  onAssignTechnician,
  currentUser,
  userType,
  onShowPatientOrders,
  handleSelectSeries,
  leadingColumns = [],
}) => {
  const gridRef = useRef<AgGridReact>(null);

  // Col-ids of the pinned-left leading icon columns; excluded from saved order.
  const leadingColIds = useMemo(
    () => new Set((leadingColumns || []).map((c: any, i: number) => c.key || c.dataIndex || `lead_${i}`)),
    [leadingColumns]
  );
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [columnApi, setColumnApi] = useState<ColumnApi | null>(null);

  // True only while we are programmatically syncing AG Grid's selection to the
  // `selectedRowKeys` prop, so handleSelectionChanged can ignore the echoed
  // event and avoid pushing the same change back to the parent.
  const isSyncingSelectionRef = useRef(false);

  // Store callbacks in a ref to avoid re-renders
  const callbacksRef = useRef({
    onOpenReport,
    onOpenViewer,
    onViewReport,
    onOpenRadiant,
    enableRadiantViewer,
    userList,
    onRadiologistAssignment,
    onAssignToSelfTechnician,
    onAssignTechnician,
    currentUser,
    onShowPatientOrders,
    handleSelectSeries,
  });

  // Update ref when callbacks change (without causing re-render)
  useEffect(() => {
    callbacksRef.current = {
      onOpenReport,
      onOpenViewer,
      onViewReport,
      onOpenRadiant,
      enableRadiantViewer,
      userList,
      onRadiologistAssignment,
      onAssignToSelfTechnician,
      onAssignTechnician,
      currentUser,
      onShowPatientOrders,
      handleSelectSeries,
    };
  }, [onOpenReport, onOpenViewer, onViewReport, onOpenRadiant, enableRadiantViewer, userList, onRadiologistAssignment, onAssignToSelfTechnician, onAssignTechnician, currentUser, onShowPatientOrders, handleSelectSeries]);

  // Context passed to all cell renderers - use ref to avoid recreation
  const gridContext = useMemo(() => ({
    get onOpenReport() { return callbacksRef.current.onOpenReport; },
    get onOpenViewer() { return callbacksRef.current.onOpenViewer; },
    get onViewReport() { return callbacksRef.current.onViewReport; },
    get onOpenRadiant() { return callbacksRef.current.onOpenRadiant; },
    get enableRadiantViewer() { return callbacksRef.current.enableRadiantViewer; },
    get userList() { return callbacksRef.current.userList; },
    get onRadiologistAssignment() { return callbacksRef.current.onRadiologistAssignment; },
    get onAssignToSelfTechnician() { return callbacksRef.current.onAssignToSelfTechnician; },
    get onAssignTechnician() { return callbacksRef.current.onAssignTechnician; },
    get currentUser() { return callbacksRef.current.currentUser; },
    get onShowPatientOrders() { return callbacksRef.current.onShowPatientOrders; },
    get handleSelectSeries() { return callbacksRef.current.handleSelectSeries; },
  }), []); // Empty deps - context object is stable, getters read from ref

  const getRowId = useCallback((params: any) => {
    return params.data?.pacs_order?.pacs_ord_id?.toString() || params.data?.id?.toString();
  }, []);

  // Tint emergency-priority rows reddish (replaces the old patient-name alert
  // icon) and ASAP-priority rows amber. EMERGENCY wins if both somehow apply.
  const getRowClass = useCallback((params: any) => {
    const priority = params.data?.pacs_order?.po_priority?.toUpperCase();
    if (priority === 'EMERGENCY') return 'pacs-emergency-row';
    if (priority === 'ASAP') return 'pacs-asap-row';
    return '';
  }, []);

  /**
   * Convert Ant Design column definitions to AG Grid ColDef format
   * Only depends on columns, fixedColumnsCount, and columnHeaderActions - NOT on callback props
   */
  const columnDefs: ColDef[] = useMemo(() => {
    const agColumns: ColDef[] = [];

    // Add checkbox selection column first (pinned left)
    agColumns.push({
      headerName: '',
      field: '__checkbox__',
      colId: '__checkbox__',
      checkboxSelection: true,
      headerCheckboxSelection: true,
      width: 50,
      pinned: 'left',
      suppressMovable: true,
      lockPosition: true,
    });

    // Nameless, pinned-left leading icon columns (before Patient ID). Rendered
    // via the column's own `render` fn; fixed width; never sortable / movable.
    (leadingColumns || []).forEach((col: any, i: number) => {
      const colKey = col.key || col.dataIndex || `lead_${i}`;
      const colWidth = col.width || 40;
      agColumns.push({
        headerName: '',
        field: colKey,
        colId: colKey,
        width: colWidth,
        minWidth: col.minWidth || colWidth,
        maxWidth: col.maxWidth || colWidth,
        pinned: 'left',
        lockPosition: true,
        suppressMovable: true,
        sortable: false,
        filter: false,
        resizable: false,
        cellRenderer: getCellRenderer(colKey, col),
        cellRendererParams: col.render ? { renderFn: col.render } : undefined,
        valueGetter: () => '',
      });
    });

    // Convert each Ant column to AG Grid format
    columns.forEach((col, index) => {
      const field = col.dataIndex || col.key || `col_${index}`;
      const colKey = col.key || field;
      const headerName = extractHeaderName(col, index);
      const isActionsColumn = colKey === 'actions_column' || colKey === 'actions';
      const isReportingStatusColumn = colKey === 'live_report_status';
      const headerAction = columnHeaderActions?.[colKey];

      // Flex (fit-to-viewport) columns carry a `flex` weight instead of a fixed
      // width — AG Grid distributes the available width across them so the table
      // fills the viewport with no horizontal scroll. Columns without `flex`
      // keep their explicit pixel width (e.g. the reporting-status sliver).
      const hasFlex = col.flex != null;
      const sizing: Partial<ColDef> = hasFlex
        ? { flex: col.flex, minWidth: col.minWidth || 70 }
        : { width: col.width || 150, minWidth: col.minWidth || col.width || 80 };

      const agCol: ColDef = {
        headerName,
        field,
        colId: colKey,
        ...sizing,
        resizable: true,
        sortable: !isActionsColumn, // Disable sorting for actions column
        filter: !isActionsColumn && !isReportingStatusColumn, // Disable filtering for actions and reporting status columns
        pinned: col.fixed === 'right' ? 'right' : col.fixed === 'left' ? 'left' : undefined,
        suppressMovable: index < fixedColumnsCount,
        cellRenderer: getCellRenderer(colKey, col),
      };

      // Use custom header component if a header action is configured
      if (headerAction) {
        agCol.headerComponent = CustomColumnHeader;
        agCol.headerComponentParams = { headerAction };
      }

      // Pass render function through cellRendererParams if column has custom render
      if (col.render) {
        agCol.cellRendererParams = { renderFn: col.render };
      }

      // Use COLUMN_VALUE_PATH_MAP for correct nested data path, fall back to dataIndex/key for top-level fields
      const valuePath = COLUMN_VALUE_PATH_MAP[colKey] || COLUMN_VALUE_PATH_MAP[field] || col.dataIndex || col.key;
      agCol.valueGetter = (params) => {
        return getNestedValue(params.data, valuePath) ?? '';
      };

      agColumns.push(agCol);
    });

    return agColumns;
  }, [columns, fixedColumnsCount, columnHeaderActions, leadingColumns]);

  // AG Grid treats pinning as column state, not column definition.
  // setColumnDefs won't move columns in/out of pinned areas.
  // We need applyColumnState to explicitly set each column's pinned state.
  useEffect(() => {
    if (!columnApi) return;

    const columnState = columnDefs.map(col => ({
      colId: col.colId || col.field,
      pinned: col.pinned || null,
    }));

    columnApi.applyColumnState({ state: columnState });
  }, [columnApi, columnDefs]);

  const onGridReady = useCallback((params: GridReadyEvent) => {
    setGridApi(params.api);
    setColumnApi(params.columnApi);
  }, []);

  const handleSelectionChanged = useCallback((event: SelectionChangedEvent) => {
    // Ignore selection events we triggered ourselves while syncing the grid to
    // the `selectedRowKeys` prop — otherwise we'd echo the change back up.
    if (isSyncingSelectionRef.current) return;
    if (!onSelectionChange) return;
    const selectedRows = event.api.getSelectedRows();
    const keys = selectedRows
      .map((row: any) => row?.pacs_order?.pacs_ord_id)
      .filter(Boolean);
    onSelectionChange(keys);
  }, [onSelectionChange]);

  // Mirror the controlled `selectedRowKeys` prop back into AG Grid's own
  // selection state. AG Grid only reports selection outward (onSelectionChanged)
  // and does not react to the prop, so when the parent clears/changes the
  // selection programmatically (e.g. auto-uncheck after opening studies) the
  // checkboxes would otherwise stay visually ticked. The isSelected() guard
  // prevents redundant work and keeps this loop-safe.
  useEffect(() => {
    if (!gridApi) return;
    const desired = new Set((selectedRowKeys || []).map(k => String(k)));
    isSyncingSelectionRef.current = true;
    try {
      gridApi.forEachNode((node: any) => {
        const id = node?.data?.pacs_order?.pacs_ord_id;
        const shouldBeSelected = id != null && desired.has(String(id));
        if (node.isSelected() !== shouldBeSelected) {
          node.setSelected(shouldBeSelected);
        }
      });
    } finally {
      isSyncingSelectionRef.current = false;
    }
  }, [gridApi, selectedRowKeys]);

  const handleRowClicked = useCallback((event: RowClickedEvent) => {
    if (!onRowClick) return;
    const target = event.event?.target as HTMLElement;
    if (target?.closest('.ag-cell[col-id="status"], .ag-cell[col-id="po_status"]')) {
      return;
    }
    if (target?.closest('button, .ant-btn, .anticon, a, input, .ant-select, .ant-tooltip, .ant-dropdown, .action-icon-button, .ant-popover')) {
      return;
    }
    onRowClick(event.data);
  }, [onRowClick]);

  const handleColumnMoved = useCallback(() => {
    if (!columnApi || !onColumnOrderChange) return;
    const allColumns = columnApi.getAllGridColumns() || [];
    const newOrder = allColumns
      .filter(col => col.getColId() !== '__checkbox__' && !leadingColIds.has(col.getColId()))
      .map(col => {
        const colDef = col.getColDef();
        return {
          key: colDef.colId || colDef.field,
          dataIndex: colDef.field,
          title: colDef.headerName,
        };
      });
    onColumnOrderChange(newOrder);
  }, [columnApi, onColumnOrderChange, leadingColIds]);
  // Every row is selectable for all roles, so the checkbox column renders
  // uniformly (no suppressed/blank checkboxes). Previously HOD users had
  // selection disabled on SIGNEDOFF rows, which left those rows with an empty
  // checkbox cell — visually inconsistent with the radiologist view.
  const isRowSelectable = useCallback((node: any) => {
    return !!node.data;
  }, []);

  const defaultColDef = useMemo<ColDef>(() => ({
    resizable: true,
    sortable: true,
    filter: true,
    suppressMenu: false,
  }), []);

  return (
    <div className="pacs-ag-grid-container">
      {loading && (
        <div className="pacs-ag-grid-loading">
          <Spin size="large" />
        </div>
      )}
      <div
        className="ag-theme-alpine pacs-ag-grid"
        style={{
          width: '100%',
          height,
        }}
      >
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          context={gridContext}
          getRowId={getRowId}
          getRowClass={getRowClass}
          rowSelection="multiple"
          isRowSelectable={isRowSelectable}
          suppressRowClickSelection={true}
          onGridReady={onGridReady}
          onSelectionChanged={handleSelectionChanged}
          onRowClicked={handleRowClicked}
          onColumnMoved={handleColumnMoved}
          animateRows={false}
          enableCellTextSelection={true}
          suppressCellFocus={true}
          rowHeight={40}
          headerHeight={32}
          suppressRowVirtualisation={true}
          suppressColumnVirtualisation={true}
        />
      </div>
    </div>
  );
};

export default PacsAgGrid;
